import {
    WIDGET_DEFINITIONS,
    hasAutoplayToggle,
    hasViewReviewLink,
    settingsGroupsFor,
    type WidgetType,
} from '@flexbyt/shared';

/**
 * The widgets, as acquisition surfaces.
 *
 * ---------------------------------------------------------------------------
 * "Carousel" and "slider" are the same widget
 * ---------------------------------------------------------------------------
 * This is the one place where the product's vocabulary and the search
 * vocabulary genuinely disagree, and it is worth being deliberate about rather
 * than picking a side.
 *
 * The product calls it the **slider**. That name is load-bearing: the custom
 * element installed on customers' websites is `<reviewsjet-slider>`, the
 * WordPress shortcode is `[rj_slider]`, and the stored widget type is
 * `'slider'`. None of that can be renamed for a marketing preference — the
 * elements are already on live sites.
 *
 * People search for **carousel**. "Google review carousel" is a phrase with
 * volume behind it; "Google review slider" is much thinner. A page that will
 * not use the word people type is a page they will not arrive on.
 *
 * So: the URL and the headline say carousel, the product says slider, and the
 * page says so plainly rather than pretending they are two things. `seoName`
 * and the widget's own `name` are separate fields here for exactly that reason,
 * and `aliases` records the other words the page should be comfortable using.
 *
 * ---------------------------------------------------------------------------
 * Eight of the nine, and the ninth is deliberate
 * ---------------------------------------------------------------------------
 * `WIDGET_DEFINITIONS` has nine entries and this has eight. The missing one is
 * the **product widget**, which needs a product page to sit on and is offered
 * to Wix stores only — so a public page selling it would be selling something
 * most visitors cannot install, and a generator for it could not produce a
 * working preview without a product to draw. It stays a dashboard feature.
 *
 * The **spotlight** and the **quote** widget were missing for no reason at all:
 * both ship in `@flexbyt/spotlight-widget` and `@flexbyt/embedded-widgets`,
 * both install on any website, and both were absent from the gallery, the
 * navigation and the free generators purely because this file was written
 * before they existed. Adding them here puts them in all three, because the
 * navigation and the sitemap are derived from this list rather than retyped.
 */

export type WidgetContent = {
  type: WidgetType;
  /**
   * The URL segment, e.g. `review-carousel` → `/widgets/review-carousel`.
   *
   * The word "review" is in the slug rather than only in the title because the
   * query people type is "review carousel", not "carousel" — and a URL that
   * contains the phrase is one of the few ranking signals that is also honestly
   * descriptive.
   */
  slug: string;
  /** The word people search for, which is not always the product's word. */
  seoName: string;
  /** Other names the same thing goes by, worth using naturally in copy. */
  aliases: string[];
  /** Where this layout earns its place. One sentence, used in listings. */
  bestFor: string;

  /*
    ---------------------------------------------------------------------------
    Page copy
    ---------------------------------------------------------------------------
    Written per widget rather than templated. Six pages that differ only by a
    substituted noun are the thin programmatic pages the brief rules out, and
    they read as thin to a person long before a search engine decides anything.

    Each of these says something that is only true of this layout.
  */
  /** The h1. */
  headline: string;
  /**
   * The run of words in `headline` that carries its argument.
   *
   * Marked with an accent stroke on the widget's page. It is stated here rather
   * than derived because the headline rarely contains the layout's own name —
   * "A wall of reviews that never leaves a gap" says nothing about masonry —
   * and the phrase worth emphasising is the promise, not the noun.
   */
  markPhrase: string;
  /** The paragraph under it. Two sentences at most. */
  intro: string;
  /** Three or four situations this layout is the right answer to. */
  useCases: { title: string; body: string }[];
  /** The honest counterpart: when to pick something else. */
  insteadWhen: string;
  /** Questions worth answering on this page specifically. */
  faqs: { q: string; a: string }[];
  /**
   * Why a control a visitor might reach for is not on this page.
   *
   * Only two layouts need one, and the reason differs, so it is written here
   * rather than derived from which controls are absent — the grid, masonry and
   * popup all lack an autoplay switch without being tickers, and deriving the
   * copy handed them the marquee's explanation.
   */
  controlNote?: string;
};

/**
 * The layouts, in the order every surface presents them.
 *
 * ---------------------------------------------------------------------------
 * The order is a decision, not an accident
 * ---------------------------------------------------------------------------
 * This array drives the gallery, the navigation menu, the footer's shortlist
 * and the sitemap, so its order is the order a visitor meets the widgets in
 * everywhere on the site — which makes it worth choosing rather than inheriting
 * from whatever order they happened to be written in.
 *
 * The **marquee leads**. It is the only layout that is already moving when the
 * page arrives, and the gallery's whole job is to make somebody believe these
 * are running widgets rather than screenshots. A still grid at the top asks
 * them to take that on trust; a ticker scrolling past proves it before they
 * have read a word. It is also the layout that suffers least from going first —
 * it is a full-width band by nature, so it fills the widest row on the page
 * without being stretched into a shape it was not drawn for.
 *
 * After it, the layouts that fill a section of a page: the three walls, then
 * the carousel. Then the two that show one review — the quote, which is a piece
 * of a page, and the popup and spotlight, which are not part of the page at all
 * but float over it. That is roughly most-to-least page real estate, which is
 * the order somebody choosing a layout is actually deciding in.
 */
export const WIDGET_CONTENT: WidgetContent[] = [
  {
    type: 'marquee',
    slug: 'review-marquee',
    seoName: 'Marquee',
    aliases: ['scrolling reviews', 'review ticker'],
    bestFor:
      'A full-width band between sections, where continuous movement catches an eye that is scrolling past.',
    headline: 'Reviews that scroll past, quietly and continuously.',
    markPhrase: 'quietly and continuously',
    intro:
      'A ticker of review cards moving steadily across the page. It works because it is the only thing on the screen that moves — an eye scrolling past a static page stops at it, and it costs one band of height to do that.',
    useCases: [
      {
        title: 'Between two sections',
        body: 'A full-width strip that breaks up a long page and does a job while it is there.',
      },
      {
        title: 'Above the fold',
        body: 'Movement in the first screenful, without a video or an animation that has to load.',
      },
      {
        title: 'When you have many short reviews',
        body: 'Short reviews suit a moving band; nobody reads a paragraph going past.',
      },
    ],
    insteadWhen:
      'If somebody needs to actually read the reviews — on a page where they are deciding — put them somewhere still. A marquee is for noticing, not for reading.',
    controlNote:
      'The marquee scrolls continuously, so there is no autoplay switch: a ticker that does not tick is a list. It does pause on hover, and stops entirely for anybody who asks their system for reduced motion.',
    faqs: [
      {
        q: 'Can I stop it moving?',
        a: 'It pauses when a visitor hovers over it, so a review that catches somebody’s eye can be read. The marquee has no on-off switch, though: a ticker that does not tick is a list, and the list layout is the better version of that.',
      },
      {
        q: 'Is it accessible?',
        a: 'It stops entirely for anybody whose system asks for reduced motion, and the reviews stay readable as text.',
      },
      {
        q: 'How wide should it be?',
        a: 'Full width. It is the one layout that looks wrong inside a narrow column, because the movement needs somewhere to go.',
      },
    ],
  },
  {
    type: 'masonry',
    slug: 'review-masonry',
    seoName: 'Masonry',
    aliases: ['wall of love', 'review wall', 'testimonial wall'],
    bestFor:
      'A dedicated reviews page, where long and short reviews should sit together without leaving gaps.',
    headline: 'A wall of reviews that never leaves a gap.',
    markPhrase: 'never leaves a gap',
    intro:
      'Reviews packed into staggered columns, each card as tall as the review inside it. Real reviews are uneven — one line from somebody in a hurry, six from somebody who really meant it — and masonry is the only layout that treats that as a feature.',
    useCases: [
      {
        title: 'A dedicated reviews page',
        body: 'The layout that makes a page of nothing but reviews look considered rather than long.',
      },
      {
        title: 'Reviews with photos',
        body: 'Cards size themselves around an image, so a review with a photo does not stretch a whole row.',
      },
      {
        title: 'When the reviews vary in length',
        body: 'A grid pads short reviews with empty space to match the tall ones. Masonry does not.',
      },
    ],
    insteadWhen:
      'If your reviews are all about the same length, the grid gives you the same wall with a tidier edge. And if you only have a handful, a wall of four cards looks like a wall that is missing something.',
    faqs: [
      {
        q: 'How many columns does it use?',
        a: 'As many as fit. It measures the space it is dropped into and reflows — three or four columns in a full-width section, one on a phone.',
      },
      {
        q: 'Can visitors open a review in full?',
        a: 'Yes. Long reviews are shortened on the card with a link that opens the whole thing, and photos open at full size.',
      },
      {
        q: 'Is it slow with a lot of reviews?',
        a: 'It loads a page at a time rather than everything at once, so a shop with two thousand reviews loads no more than one with twenty.',
      },
    ],
  },
  {
    type: 'grid',
    slug: 'review-grid',
    seoName: 'Grid',
    aliases: ['review grid', 'testimonial grid'],
    bestFor:
      'Reviews of a similar length, where an even, predictable arrangement reads as tidy rather than rigid.',
    headline: 'An even grid of reviews, tidy at every width.',
    markPhrase: 'tidy at every width',
    intro:
      'Equal cards in equal rows. When your reviews are a similar length it is the calmest way to show a lot of them at once — nothing staggers, nothing reflows unexpectedly, and the section has a straight edge on all four sides.',
    useCases: [
      {
        title: 'A reviews section on a homepage',
        body: 'Six or nine reviews in a block that lines up with the rest of the page’s grid.',
      },
      {
        title: 'Product or service pages',
        body: 'A predictable shape you can place under a description without redesigning the page around it.',
      },
      {
        title: 'When reviews are a similar length',
        body: 'Consistent cards look deliberate. Wildly uneven ones look like something went wrong.',
      },
    ],
    insteadWhen:
      'If your reviews vary a lot in length, the grid pads the short ones with empty space to match the tall ones. Masonry handles that without the gaps.',
    faqs: [
      {
        q: 'How many columns?',
        a: 'Three on a desktop screen, two on a tablet and one on a phone. It follows the width it is given rather than a fixed setting.',
      },
      {
        q: 'What happens to a very long review?',
        a: 'It is shortened on the card, with a link that opens the full text. That is what keeps the rows even.',
      },
      {
        q: 'Can I choose which reviews appear?',
        a: 'Yes — only reviews you have published are shown, and you can narrow a widget to particular tags.',
      },
    ],
  },
  {
    type: 'list',
    slug: 'review-list',
    seoName: 'List',
    aliases: ['review list', 'testimonial list'],
    bestFor:
      'Long, detailed reviews, and any page whose traffic is mostly on a phone.',
    headline: 'One review after another, the way people read.',
    markPhrase: 'the way people read',
    intro:
      'A single column, newest first, with nothing boxed. It is the layout for reviews people are meant to sit and read — and the one that needs no adjustment at all on a phone, because a phone is already one column.',
    useCases: [
      {
        title: 'Long, detailed reviews',
        body: 'Full width for the text, so a considered review is not squeezed into a third of the page.',
      },
      {
        title: 'Mobile-heavy traffic',
        body: 'Every other layout collapses to one column on a phone. This one starts there.',
      },
      {
        title: 'Inside a narrow column',
        body: 'A sidebar, or a page with a reading measure — the list fits where a grid cannot.',
      },
    ],
    insteadWhen:
      'If you want visitors to take in a lot of reviews at a glance rather than read a few, use the grid or the masonry wall. A list of forty reviews is a long page.',
    controlNote:
      'The list has no card border or corner radius. Rows are separated by space rather than boxes, which is what keeps a column of long text readable — and the real widget has never drawn one, on any version of ReviewsJet.',
    faqs: [
      {
        q: 'Why do the rows have no border?',
        a: 'The list has never drawn one, on any version of ReviewsJet — rows are separated by space rather than boxes, which is what keeps a column of long text readable. The border and radius settings exist on other layouts.',
      },
      {
        q: 'What order are reviews in?',
        a: 'Newest first by default. You can sort by rating instead.',
      },
      {
        q: 'Does it work in a sidebar?',
        a: 'Yes. It is the layout least bothered by a narrow container.',
      },
    ],
  },
  {
    type: 'slider',
    slug: 'review-carousel',
    seoName: 'Carousel',
    aliases: ['slider', 'review slider', 'testimonial carousel'],
    bestFor:
      'A homepage or landing page where there is room for a few reviews but not for all of them.',
    headline: 'A review carousel that rotates through your best.',
    markPhrase: 'rotates through your best',
    intro:
      'Three reviews at a time, moving on by themselves. It is the layout for a page that has room for some social proof but not for a wall of it — a homepage band, a landing page, the space above a pricing table.',
    useCases: [
      {
        title: 'Homepage social proof',
        body: 'One band between two sections. Visitors see three reviews without the page growing by a screenful.',
      },
      {
        title: 'Landing pages',
        body: 'Reviews near the call to action, in a fixed height that does not push the button below the fold.',
      },
      {
        title: 'When you have a lot of reviews',
        body: 'A carousel shows a rotating sample, so two hundred reviews take the same space as six.',
      },
    ],
    insteadWhen:
      'If you want every review visible at once — on a dedicated reviews page, say — the masonry wall or the grid will serve you better. A carousel hides most of what it holds.',
    faqs: [
      {
        q: 'Does it rotate on its own?',
        a: 'Yes, and you can turn that off. Autoplay and the delay between slides are both settings; with autoplay off, visitors move it themselves.',
      },
      {
        q: 'How many reviews does it show?',
        a: 'Three at a time by default on a desktop screen, and one at a time on a phone. It draws from however many published reviews you have.',
      },
      {
        q: 'Why does ReviewsJet call it a slider?',
        a: 'The widget installed on your site is <reviewsjet-slider>, and its WordPress shortcode is [rj_slider]. Carousel and slider are the same thing here — we use both words because both are what people call it.',
      },
    ],
  },
  {
    type: 'quote',
    slug: 'review-quote',
    seoName: 'Quote',
    aliases: ['testimonial block', 'pull quote', 'featured review'],
    bestFor:
      'A page that needs one line of proof set into it, not a section of them.',
    headline: 'One review, set as part of the page.',
    markPhrase: 'part of the page',
    intro:
      'Every other layout answers "what do people say about this business" by showing several answers and letting the reader average them. This one prints a single review the way a book jacket prints one line from one critic — and picks a different one on each visit, so the page is never quite the same twice.',
    useCases: [
      {
        title: 'Beside a price',
        body: 'One sentence from somebody who paid it, in the page\u2019s own typography, where a grid of cards would break the layout in half.',
      },
      {
        title: 'Between two sections',
        body: 'A full-width band carrying one quotation, which is a piece of the page rather than a component dropped into it.',
      },
      {
        title: 'When you have very few reviews',
        body: 'Four reviews make a thin wall and one excellent quotation. This is the layout that does not advertise how many you have.',
      },
    ],
    insteadWhen:
      'If the reader is weighing you against somebody else, one review will not do it — volume is itself the argument, and the grid or the masonry wall makes it. The quote persuades somebody who is already half convinced.',
    faqs: [
      {
        q: 'Which review does it show?',
        a: 'A different one each time the page loads, taken at random from the reviews you published. So a returning visitor rarely reads the same one twice, and no single review has to carry the whole page.',
      },
      {
        q: 'Can I pick the review myself?',
        a: 'You choose the pool: limit the widget to a tag and it draws only from those. Pinning one exact review for ever is not what this layout is for — a quotation that never changes is a sentence you may as well type into the page.',
      },
      {
        q: 'How many templates are there?',
        a: 'Six, from a quiet editorial line to a full-width billboard. They differ enough that the same review reads as a different piece of design in each.',
      },
    ],
    controlNote:
      'The quote widget draws no \u201cWrite a review\u201d link. Every other layout is a wall somebody is already reading reviews on, where an invitation is the next sentence; this one is set into a section that has its own call to action, and a second one underneath would compete with it.',
  },
  {
    type: 'popup',
    slug: 'review-popup',
    seoName: 'Popup',
    aliases: ['review tab', 'floating reviews', 'review badge'],
    bestFor:
      'Every page at once, without giving up any of the layout to do it.',
    headline: 'Your reviews on every page, taking up no room at all.',
    markPhrase: 'no room at all',
    intro:
      'A small tab fixed to the edge of the window that opens a panel of reviews. It is the only layout that costs you no layout: it sits on every page of your site without you finding a space for it on any of them.',
    useCases: [
      {
        title: 'Sites with no obvious space',
        body: 'A checkout, a booking flow, a documentation page — anywhere a reviews section would be in the way.',
      },
      {
        title: 'Every page at once',
        body: 'Installed once, site-wide, rather than placed page by page.',
      },
      {
        title: 'Alongside another layout',
        body: 'Plenty of sites run a wall of reviews on one page and the popup everywhere else.',
      },
    ],
    insteadWhen:
      'If reviews are part of the argument a page is making — a landing page, a product page — put them in the page. A visitor has to choose to open the popup, and most will not.',
    faqs: [
      {
        q: 'Where does the tab sit?',
        a: 'Left edge, right edge, or either bottom corner. That is a setting.',
      },
      {
        q: 'Can I hide it on phones?',
        a: 'Yes, and it is worth considering — a floating tab costs more of a small screen than it does of a large one. There is a setting for exactly that.',
      },
      {
        q: 'Does it slow the page down?',
        a: 'It loads after the page itself and fetches reviews only when somebody opens it.',
      },
    ],
  },
  {
    type: 'spotlight',
    slug: 'review-spotlight',
    seoName: 'Spotlight',
    aliases: ['review notification', 'social proof popup', 'review toast'],
    bestFor:
      'Pages with no room for reviews at all, where proof still has to reach the reader.',
    headline: 'One review, sliding in while somebody is deciding.',
    markPhrase: 'while somebody is deciding',
    intro:
      'A single review card that appears in a corner, stays a few seconds and leaves again, then comes back with a different one. It is the only layout that reaches a visitor who was never going to scroll to your reviews section.',
    useCases: [
      {
        title: 'Checkout and booking flows',
        body: 'A page where a section of reviews would be a distraction, but a reminder that other people went through with it is not.',
      },
      {
        title: 'Pages you did not design',
        body: 'A hosted booking engine, a store template, anything you cannot put a widget into. The spotlight needs no space in the layout because it does not use any.',
      },
      {
        title: 'A second appearance',
        body: 'Plenty of sites run a wall on the reviews page and the spotlight everywhere else, so the two never compete for the same screen.',
      },
    ],
    insteadWhen:
      'If a visitor is on the page specifically to read reviews, give them all of them — the spotlight shows one at a time and takes its own pace, which is the opposite of what somebody comparing options wants.',
    faqs: [
      {
        q: 'Is it not just a notification popup?',
        a: 'It looks like one and it differs in the way that matters: every card is a real review somebody wrote and published, on a platform you connected. Nothing is invented and nothing is a counter of people who "just signed up".',
      },
      {
        q: 'Which corner does it appear in?',
        a: 'Any of the four, and that is a setting. So is how long each review stays and how long the gap between them is.',
      },
      {
        q: 'Can a visitor read the whole review?',
        a: 'Yes — clicking the card opens the full review, and the card holds still while it is being hovered rather than sliding away mid-sentence.',
      },
      {
        q: 'Does it show on phones?',
        a: 'It can, and it is worth thinking about: a card in the corner of a small screen covers more of the page than it does on a laptop.',
      },
    ],
  },
];

/**
 * How many layouts this site offers, as a number and as the word for it.
 *
 * ---------------------------------------------------------------------------
 * Why copy reads this instead of saying "six"
 * ---------------------------------------------------------------------------
 * The site said "six" in eleven places — a headline, a meta description, four
 * button labels, two comparison rows and an agency FAQ — and every one of them
 * was written by hand while the list itself was already the source of truth for
 * the navigation, the gallery, the sitemap and the generators. Adding the
 * spotlight and the quote widget made all eleven wrong at once, and the sort of
 * wrong nothing catches: a page that confidently offers six layouts and then
 * renders eight is a page a reader stops trusting for reasons they cannot name.
 *
 * The word is spelled out rather than the numeral because these appear in
 * sentences, where "Eight ways to show your reviews" reads and "8 ways" does
 * not. The map runs out at twelve on purpose: past that a headline should stop
 * counting and start saying "every layout", and being forced to come back here
 * is the reminder.
 */
export const WIDGET_COUNT = WIDGET_CONTENT.length;

const NUMBER_WORDS: Record<number, string> = {
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
  11: 'eleven',
  12: 'twelve',
};

export const WIDGET_COUNT_WORD: string =
  NUMBER_WORDS[WIDGET_COUNT] ?? String(WIDGET_COUNT);

/** Capitalised, for the start of a sentence or a headline. */
export const WIDGET_COUNT_WORD_CAPITALISED: string =
  WIDGET_COUNT_WORD.charAt(0).toUpperCase() + WIDGET_COUNT_WORD.slice(1);

export function widgetContentBySlug(slug: string): WidgetContent | undefined {
  return WIDGET_CONTENT.find((widget) => widget.slug === slug);
}

export function widgetContentByType(type: WidgetType): WidgetContent | undefined {
  return WIDGET_CONTENT.find((widget) => widget.type === type);
}

/**
 * The one builder that starts with no layout chosen.
 *
 * ---------------------------------------------------------------------------
 * Two doors into the same room
 * ---------------------------------------------------------------------------
 * Somebody arriving from the homepage has just understood what ReviewsJet is
 * and has no opinion yet about masonry versus grid — they need a builder that
 * opens with the question "which of these?" and lets them find out.
 *
 * Somebody arriving from a search for "review masonry generator" has already
 * decided, and sending them to a chooser is making them re-answer a question
 * they typed into Google. They get `generatorPath` below, which opens on their
 * layout.
 *
 * Same tool, two entrances, and the entrance is the whole difference — which is
 * why this is a constant rather than a special case inside the generator.
 */
export const WIDGET_BUILDER_PATH = '/tools/review-widget-generator';

/** The generator path for one layout, e.g. `/tools/review-carousel-generator`. */
export function generatorPath(widget: WidgetContent): string {
  // `slug` already carries the "review-" prefix, so the generator route reads
  // `/tools/review-carousel-generator` rather than doubling the word.
  return `/tools/${widget.slug}-generator`;
}

/** The gallery path for a layout, e.g. `/widgets/review-carousel`. */
export function widgetPath(widget: WidgetContent): string {
  return `/widgets/${widget.slug}`;
}

/**
 * The product's own description of a layout.
 *
 * Read through rather than copied, so the gallery and the dashboard's widget
 * chooser describe each layout with the same sentence.
 */
export function widgetDescription(type: WidgetType): string {
  return WIDGET_DEFINITIONS[type].description;
}

// ---------------------------------------------------------------------------
// What a layout can actually be customised
// ---------------------------------------------------------------------------

/**
 * The controls a widget page may offer.
 *
 * ---------------------------------------------------------------------------
 * Derived from the product, never asserted
 * ---------------------------------------------------------------------------
 * Every flag below is computed from `@flexbyt/shared` — the same functions
 * the dashboard's settings panel uses to decide which sections to render. That
 * matters more here than it does in the dashboard: a marketing page offering a
 * control the real widget does not have is a promise the product then breaks,
 * and the person finding out is a new customer looking for the switch they were
 * shown.
 *
 * Three of these are genuinely restricted, and all three are easy to get wrong:
 *
 *   autoplay      the slider only. The marquee scrolls by definition, so it has
 *                 no on/off — a ticker that does not tick is a list.
 *   position      the popup only. Nothing else is anchored to the viewport.
 *   border        every layout except the list, which has never drawn one on
 *                 any version of ReviewsJet. The setting exists on the record;
 *                 the widget ignores it, so showing the control would preview an
 *                 effect no account can reproduce.
 *   viewReview    masonry and popup only — the two that show a photo big enough
 *                 that somebody might want the review behind it.
 */
export type WidgetControls = {
  /** Dark cards, and the card/border/text colours behind them. */
  colour: boolean;
  /** Reviewer photo, name, source mark, rating, date, overall header. */
  content: boolean;
  /** Rounded corners and a card border. Not the list. */
  border: boolean;
  /** Autoplay and its delay. The slider alone. */
  autoplay: boolean;
  /** Which edge the tab is fixed to. The popup alone. */
  position: boolean;
  /** The "View review" link under a card. Masonry and popup. */
  viewReview: boolean;
};

export function controlsFor(type: WidgetType): WidgetControls {
  const groups = settingsGroupsFor(type);

  return {
    colour: groups.includes('colour'),
    content: groups.includes('content'),
    // Not derivable from the groups — the list is in the `colour` group and
    // still ignores the border, so this is the one that has to be stated.
    border: type !== 'list',
    autoplay: hasAutoplayToggle(type),
    position: groups.includes('placement'),
    viewReview: hasViewReviewLink(type),
  };
}

/** How many reviews this layout draws before asking for more. */
export function defaultCountFor(type: WidgetType): number {
  return WIDGET_DEFINITIONS[type].defaultCount;
}
